import { useEffect, useState } from "react";
import { Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { StarDisplay } from "./StarRating";
import { api, errMsg } from "../lib/api";

const ALL = [1, 2, 3, 4, 5];

export const alertSummary = (scores = [], keywords = []) =>
  (scores.length === 5 ? "Every survey" : scores.length === 0 ? "No score alerts" : `${scores.join(", ")}-star reviews`) +
  (keywords.length ? ` · Keywords: ${keywords.join(", ")}` : "");

const Row = ({ id, checked, onChange, children }) => (
  <label htmlFor={id} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3.5 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors">
    <Checkbox id={id} checked={checked} onCheckedChange={onChange} data-testid={id} />
    {children}
  </label>
);

export const AlertSettingsDialog = ({ loc, multi, onSaved }) => {
  const current = loc.alert_scores || [1, 2];
  const [open, setOpen] = useState(false);
  const [scores, setScores] = useState(current);
  const [applyAll, setApplyAll] = useState(false);
  const [keywords, setKeywords] = useState(["", "", ""]);
  const [saving, setSaving] = useState(false);
  const padKw = (k = []) => [...k, "", "", ""].slice(0, 3);

  useEffect(() => { if (open) { setScores(loc.alert_scores || [1, 2]); setKeywords(padKw(loc.alert_keywords)); setApplyAll(false); } }, [open, loc]);

  const toggle = (n) => setScores((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n].sort()));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/business/locations/${loc.id}/alerts`, { scores, keywords: keywords.map((k) => k.trim()).filter(Boolean), apply_all: applyAll });
      toast.success(applyAll ? "Alert settings saved for all locations" : "Alert settings saved");
      setOpen(false);
      await onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-5 flex flex-wrap items-center justify-between gap-3" data-testid="alert-settings-panel">
      <div className="flex items-start gap-3 min-w-0">
        <span className="grid place-items-center w-9 h-9 shrink-0 rounded-xl bg-[#E3EEF3] text-[#2E6F8E]"><Bell className="w-4 h-4" /></span>
        <div className="min-w-0">
          <p className="font-semibold text-[#1A2B25]">Email alerts</p>
          <p className="text-sm text-[#4B5A54]" data-testid="alert-settings-summary">{alertSummary(current, loc.alert_keywords)}</p>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button className="btn-secondary whitespace-nowrap" data-testid="alert-settings-button">Choose alerts</button>
        </DialogTrigger>
        <DialogContent className="rounded-3xl sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="alert-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Which reviews should we email you?</DialogTitle>
            <DialogDescription>Alerts for {loc.label} go to {loc.alert_email || loc.owner_email}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Row id="alert-all-checkbox" checked={scores.length === 5} onChange={(v) => setScores(v ? ALL : [])}>
              <span className="font-semibold">All surveys</span>
            </Row>
            <div className="grid grid-cols-1 gap-2 pl-0 sm:pl-4">
              {[...ALL].reverse().map((n) => (
                <Row key={n} id={`alert-score-checkbox-${n}`} checked={scores.includes(n)} onChange={() => toggle(n)}>
                  <StarDisplay value={n} />
                  <span className="text-sm text-[#4B5A54]">{n} star{n > 1 ? "s" : ""}</span>
                </Row>
              ))}
            </div>
            {scores.length === 0 && !keywords.some((k) => k.trim()) && <p className="text-sm text-amber-700" data-testid="alert-none-warning">You won't receive any email alerts for this location.</p>}
            <div className="pt-3">
              <p className="text-sm font-semibold text-[#1A2B25]">Keyword alerts <span className="font-normal text-[#778580]">(up to 3)</span></p>
              <p className="text-xs text-[#778580] mb-2">Get an instant email when a review mentions any of these words, whatever its score.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {keywords.map((k, i) => (
                  <input key={i} value={k} maxLength={40} placeholder={["e.g. rude", "e.g. dirty", "e.g. refund"][i]}
                    onChange={(e) => setKeywords((ks) => ks.map((x, j) => (j === i ? e.target.value : x)))}
                    className="input-base" data-testid={`alert-keyword-input-${i}`} />
                ))}
              </div>
            </div>
            {multi && (
              <label htmlFor="alert-apply-all-checkbox" className="flex items-center gap-3 pt-2 text-sm cursor-pointer">
                <Checkbox id="alert-apply-all-checkbox" checked={applyAll} onCheckedChange={(v) => setApplyAll(!!v)} data-testid="alert-apply-all-checkbox" />
                Apply to all my locations
              </label>
            )}
          </div>
          <button onClick={save} disabled={saving} className="btn-primary w-full justify-center !py-3" data-testid="alert-settings-save">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save alert settings"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
